import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiV2 from '../../../services/apiV2';
import { PathwayWorkspace } from './PathwayWorkspace';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { DailyChecklist } from './DailyChecklist';
import { dailyChecklistApi } from '../../../services/dailyChecklist';

/** Existing goals, assignment tabs and non-unit classrooms keep their established workspace. */
export function ClassroomToday({ fallback }: { fallback: React.ReactNode }) {
  const [params] = useSearchParams();
  const requested = Number(params.get('section'));
  const tab = params.get('tab');
  const eligible = isUiRedesignPageEnabled('v2_student_dashboard') && !params.has('legacy') && (!tab || ['today', 'workspace', 'progress', 'goal-home'].includes(tab));
  const [context, setContext] = useState<any>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!eligible) return;
    let active = true;
    setContext(null); setError(false);
    let saved = 0;
    try { saved = Number(sessionStorage.getItem('student-classroom-section')); } catch { /* Optional preference. */ }
    dailyChecklistApi.context(requested || saved || undefined).then(data => { if (active) setContext(data); }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [requested, eligible, retry]);
  if (!eligible) return <LegacyClassroomToday fallback={fallback} />;
  if (error) return <p role="alert" className="p-6">Your daily workspace could not load. <button className="underline" onClick={() => setRetry(v => v + 1)}>Try again</button></p>;
  if (!context || (requested && context.section_id && requested !== context.section_id)) return <p role="status" className="p-6">Loading your daily workspace…</p>;
  if (context.enabled) return <DailyChecklist key={`${context.section_id}:${context.local_school_date}`} context={context} />;
  return <LegacyClassroomToday fallback={fallback} />;
}

function LegacyClassroomToday({ fallback }: { fallback: React.ReactNode }) {
  const [params] = useSearchParams();
  const [pathways, setPathways] = useState<any[] | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const requested = Number(params.get('section'));
  useEffect(() => {
    let active = true;
    const load = () => apiV2.get('/my/units/').then(({ data }) => { if (active) { setPathways(data.pathways || []); setError(false); } }).catch(() => { if (active) setError(true); });
    void load();
    const timer = window.setInterval(() => { if (document.visibilityState !== 'hidden') void load(); }, 15000);
    return () => { active = false; clearInterval(timer); };
  }, [retry]);
  if (!isUiRedesignPageEnabled('v2_student_dashboard') || params.get('tab') || (pathways && (!pathways.length || (requested && !pathways.some(p => p.section_id === requested))))) return <>{fallback}</>;
  if (error) return <><p role="alert" className="m-4 rounded border p-4">Today’s unit work could not be refreshed. <button className="underline" onClick={() => setRetry(v => v + 1)}>Try again</button></p>{pathways?.length ? <PathwayWorkspace pathways={pathways} view="today" refresh={() => setRetry(v => v + 1)} /> : fallback}</>;
  return pathways ? <PathwayWorkspace pathways={pathways} view="today" refresh={() => setRetry(v => v + 1)} /> : <p role="status" className="p-6">Loading your classroom…</p>;
}
